/**
 * DLN Wishlist V3.2
 * Stable product-level wishlist.
 * Supports the actual DLN button attributes: data-wishlist-*.
 * No MutationObserver.
 * Includes DLN wishlist analytics.
 */
(() => {
  const STORAGE_KEY = 'dln_wishlist_v1';
  const EVENT_NAME = 'dln:wishlist-change';

  const track = (name, data = {}) => {
    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, {
          ...data,
          source: data.source || 'dln_wishlist',
          page_path: window.location.pathname,
          page_locale: document.documentElement.lang || ''
        }).catch?.(() => {});
      }
    } catch (_) {}
  };

  const get = () => {
    try {
      const value = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]'
      );

      return Array.isArray(value) ? value : [];
    } catch (_) {
      return [];
    }
  };

  const emit = items => {
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: {
          items
        }
      })
    );
  };

  const save = items => {
    const clean = Array.isArray(items) ? items : [];

    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(clean)
      );
    } catch (_) {}

    sync(false);
    emit(clean);

    return clean;
  };

  const productIdFor = button =>
    String(
      button.dataset.wishlistProductId ||
      button.dataset.productId ||
      button.closest('[data-product-id]')?.dataset.productId ||
      ''
    );

  const currentVariantFor = button => {
    const scope =
      button.closest(
        '[data-testid="product-information-details"]'
      ) ||
      button.closest('.product-details') ||
      button.closest('product-form-component') ||
      button.closest('product-card') ||
      button.closest('[data-product-id]') ||
      document;

    const selected =
      scope.querySelector(
        'form[action*="/cart/add"] input[name="id"]:checked'
      ) ||
      scope.querySelector(
        'form[action*="/cart/add"] select[name="id"]'
      ) ||
      scope.querySelector(
        'form[action*="/cart/add"] input[name="id"]'
      );

    return String(
      selected?.value ||
      button.dataset.wishlistVariantId ||
      button.dataset.variantId ||
      ''
    );
  };

  const readButtonItem = button => ({
    id: productIdFor(button),

    variantId: currentVariantFor(button),

    title:
      button.dataset.wishlistTitle ||
      button.dataset.productTitle ||
      '',

    url:
      button.dataset.wishlistUrl ||
      button.dataset.productUrl ||
      '',

    image:
      button.dataset.wishlistImage ||
      button.dataset.productImage ||
      '',

    price:
      button.dataset.wishlistPrice ||
      button.dataset.productPrice ||
      '',

    line: (
      button.dataset.wishlistLine ||
      button.dataset.productLine ||
      ''
    ).toLowerCase(),

    available:
      (
        button.dataset.wishlistAvailable ??
        button.dataset.available ??
        'true'
      ) !== 'false'
  });

  const contains = id => {
    const pid = String(id || '');

    return get().some(
      item => String(item?.id || '') === pid
    );
  };

  const remove = id => {
    const pid = String(id || '');
    const current = get();

    const removed = current.find(
      item => String(item?.id || '') === pid
    );

    const next = save(
      current.filter(
        item => String(item?.id || '') !== pid
      )
    );

    if (removed) {
      track('dln:wishlist_remove', {
        product_id: pid,
        variant_id: String(
          removed.variantId || ''
        ),
        product_title: removed.title || '',
        product_line: removed.line || '',
        price_display: removed.price || '',
        wishlist_size: next.length
      });
    }

    return next;
  };

  const add = item => {
    if (!item?.id) return get();

    const pid = String(item.id);

    const next = [
      {
        ...item,
        id: pid,
        savedAt: Date.now()
      },

      ...get().filter(
        existing =>
          String(existing?.id || '') !== pid
      )
    ];

    const saved = save(next);

    track('dln:wishlist_add', {
      product_id: pid,
      variant_id: String(
        item.variantId || ''
      ),
      product_title: item.title || '',
      product_line: item.line || '',
      price_display: item.price || '',
      wishlist_size: saved.length
    });

    return saved;
  };

  const repairVisibleItems = () => {
    const items = get();

    if (!items.length) {
      return items;
    }

    let changed = false;

    const byId = new Map(
      items.map(
        (item, index) => [
          String(item?.id || ''),
          index
        ]
      )
    );

    document
      .querySelectorAll(
        '[data-dln-wishlist-toggle]'
      )
      .forEach(button => {
        const fresh = readButtonItem(button);

        if (
          !fresh.id ||
          !byId.has(fresh.id)
        ) {
          return;
        }

        const index = byId.get(fresh.id);
        const old = items[index] || {};

        const repaired = {
          ...old
        };

        [
          'variantId',
          'title',
          'url',
          'image',
          'price',
          'line'
        ].forEach(key => {
          if (
            !repaired[key] &&
            fresh[key]
          ) {
            repaired[key] = fresh[key];
            changed = true;
          }
        });

        if (
          typeof repaired.available !==
          'boolean'
        ) {
          repaired.available =
            fresh.available;

          changed = true;
        }

        items[index] = repaired;
      });

    if (changed) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(items)
        );
      } catch (_) {}

      emit(items);
    }

    return items;
  };

  function sync(allowRepair = true) {
    const items = allowRepair
      ? repairVisibleItems()
      : get();

    const ids = new Set(
      items.map(
        item => String(item?.id || '')
      )
    );

    document
      .querySelectorAll(
        '[data-dln-wishlist-toggle]'
      )
      .forEach(button => {
        const active = ids.has(
          productIdFor(button)
        );

        /*
         * IMPORTANT:
         * This is the original DLN active-heart logic.
         */
        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );

        button.dataset.wishlistActive =
          active ? 'true' : 'false';

        const label =
          button.querySelector(
            '[data-wishlist-label]'
          );

        const sr =
          button.querySelector(
            '[data-wishlist-sr]'
          );

        const text = active
          ? (
              button.dataset
                .wishlistRemoveLabel ||
              'Remove from wishlist'
            )
          : (
              button.dataset
                .wishlistAddLabel ||
              'Add to wishlist'
            );

        if (label) {
          label.textContent = text;
        }

        if (sr) {
          sr.textContent = text;
        }
      });

    document
      .querySelectorAll(
        '[data-dln-wishlist-count]'
      )
      .forEach(element => {
        element.textContent =
          String(items.length);

        element.hidden =
          items.length === 0;
      });
  }

  document.addEventListener(
    'click',
    event => {
      const button =
        event.target.closest(
          '[data-dln-wishlist-toggle]'
        );

      if (!button) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const item =
        readButtonItem(button);

      if (!item.id) {
        return;
      }

      if (contains(item.id)) {
        remove(item.id);
      } else {
        add(item);
      }
    }
  );

  window.addEventListener(
    'storage',
    event => {
      if (
        event.key === STORAGE_KEY
      ) {
        sync();
      }
    }
  );

  document.addEventListener(
    'shopify:section:load',
    () => sync()
  );

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => sync(),
      {
        once: true
      }
    );
  } else {
    sync();
  }

  window.DLNWishlist = {
    version: '3.2.0',
    get,
    save,
    add,
    remove,
    contains,
    sync
  };
})();